import React from 'react';
import { Tabs, Button, Modal } from 'antd';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { DAG_RUN_TASK_DETAIL_TAB, DAG_RUN_TASK_DETAIL_TABS } from '../constants';
import { ClearTaskDialog } from '../dag-detail/clear-task-dialog';
import { TaskInstanceHeader } from './task-instance-header';
import { LogsTab } from './logs-tab';
import { XcomTab } from './xcom-tab';
import { TaskEventsTab } from './task-events-tab';
import { CodeTab } from './code-tab';
import { DetailsTab } from './details-tab';
import { useDagRunTaskDetail } from './use-dag-run-task-detail';
import styles from '../styles.module.scss';

const { TabPane } = Tabs;

const CONFIRM_COPY: Record<string, { title: string; okColor: string; okGlow: string }> = {
    success: { title: 'Mark task as success?', okColor: '#08c46a', okGlow: 'rgba(8, 196, 106, 0.72)' },
    failed: { title: 'Mark task as failed?', okColor: '#ff4d4f', okGlow: 'rgba(255, 77, 79, 0.72)' },
};

/*
 * DAG-run-task detail screen — the most detailed screen in the dashboard: a single task
 * instance within a single run, with Logs/XCom/Task Events/Code/Details
 */
export function AirflowDagRunTaskDetailScreen() {
    const {
        dagId,
        runId,
        taskId,
        mapIndex,
        taskInstance,
        isLoadingTask,
        refreshKey,
        clearing,
        confirmAction,
        setConfirmAction,
        activeTab,
        handleTabChange,
        handleRefresh,
        handleClearRequest,
        handleCloseClearDialog,
        handleTaskCleared,
        handleConfirmAction,
        handleBack,
    } = useDagRunTaskDetail();

    return (
        <div className={`${styles.page} airflowDashboardRoot`}>
            <div className={styles.breadcrumb}>
                <span className={styles.breadcrumbLink} onClick={handleBack}>
                    <FiArrowLeft /> Back to Run
                </span>
                <Button size="small" onClick={handleRefresh}>
                    <FiRefreshCw />
                </Button>
            </div>
            <TaskInstanceHeader
                dagId={dagId}
                runId={runId}
                taskId={taskId}
                taskInstance={taskInstance}
                isLoading={isLoadingTask}
                onClear={handleClearRequest}
                onMarkSuccess={() => setConfirmAction('success')}
                onMarkFailed={() => setConfirmAction('failed')}
            />
            <Tabs activeKey={activeTab} onChange={handleTabChange}>
                {DAG_RUN_TASK_DETAIL_TABS.map((tab) => (
                    <TabPane tab={tab.label} key={tab.value}>
                        {tab.value === DAG_RUN_TASK_DETAIL_TAB.logs && <LogsTab dagId={dagId} runId={runId} taskId={taskId} taskInstance={taskInstance} mapIndex={mapIndex} />}
                        {tab.value === DAG_RUN_TASK_DETAIL_TAB.xcom && <XcomTab dagId={dagId} runId={runId} taskId={taskId} mapIndex={mapIndex} refreshKey={refreshKey} />}
                        {tab.value === DAG_RUN_TASK_DETAIL_TAB.assetEvents && <TaskEventsTab dagId={dagId} runId={runId} taskId={taskId} refreshKey={refreshKey} />}
                        {tab.value === DAG_RUN_TASK_DETAIL_TAB.code && <CodeTab dagId={dagId} refreshKey={refreshKey} />}
                        {tab.value === DAG_RUN_TASK_DETAIL_TAB.details && (
                            <DetailsTab dagId={dagId} runId={runId} taskId={taskId} mapIndex={mapIndex} taskInstance={taskInstance} isLoading={isLoadingTask} refreshKey={refreshKey} />
                        )}
                    </TabPane>
                ))}
            </Tabs>
            {clearing && taskInstance && <ClearTaskDialog dagId={dagId} task={taskInstance} onClose={handleCloseClearDialog} onCleared={handleTaskCleared} />}
            {confirmAction && (
                <Modal
                    visible
                    className="airflowDashboardRoot"
                    title={CONFIRM_COPY[confirmAction].title}
                    onCancel={() => setConfirmAction(null)}
                    onOk={handleConfirmAction}
                    okText="Confirm"
                    okButtonProps={{
                        style: {
                            backgroundColor: CONFIRM_COPY[confirmAction].okColor,
                            borderColor: CONFIRM_COPY[confirmAction].okColor,
                            boxShadow: `0 1px 4px 0 ${CONFIRM_COPY[confirmAction].okGlow}`,
                        },
                    }}
                />
            )}
        </div>
    );
}
